import React, { useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    message: "",
    company: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [news, setNews] = useState({ email: "", company: "" });
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsDone, setNewsDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please tell us your name.";
    if (!EMAIL_RE.test(form.email.trim())) er.email = "Enter a valid email address.";
    if (!form.message.trim()) er.message = "Add a short message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", event_type: "", event_date: "", message: "", company: "" });
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitNews = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(news.email.trim())) {
      toast.error("Enter a valid email address.");
      return;
    }
    setNewsLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, news);
      toast.success(res.data?.message || "You're on the list!");
      setNewsDone(true);
      setNews({ email: "", company: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Something went wrong. Please try again.");
    } finally {
      setNewsLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative py-24 md:py-32 bg-gradient-to-b from-[#0a0a0b] to-[#0d0d10]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Booking form */}
          <div>
            <Reveal>
              <span className="text-sm font-bold uppercase tracking-[0.25em] text-[var(--sb-red)]">Booking</span>
              <h2 className="font-display text-5xl md:text-6xl text-white mt-3 leading-[0.95]">
                Book The Band
              </h2>
              <p className="text-white/65 mt-4">
                Festival, private party or venue night — tell us about your event and we'll get right back to you.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="booking-form" noValidate>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={set("company")}
                  className="hidden"
                  aria-hidden
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-white/80">Name *</Label>
                    <Input
                      id="name" data-testid="booking-name" value={form.name} onChange={set("name")}
                      placeholder="Your name"
                      className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 h-12"
                    />
                    {errors.name && <p className="text-[var(--sb-red)] text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white/80">Email *</Label>
                    <Input
                      id="email" data-testid="booking-email" value={form.email} onChange={set("email")}
                      placeholder="you@email.com"
                      className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 h-12"
                    />
                    {errors.email && <p className="text-[var(--sb-red)] text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-white/80">Phone</Label>
                    <Input
                      id="phone" data-testid="booking-phone" value={form.phone} onChange={set("phone")}
                      placeholder="Optional"
                      className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_type" className="text-white/80">Event Type</Label>
                    <Input
                      id="event_type" data-testid="booking-eventtype" value={form.event_type} onChange={set("event_type")}
                      placeholder="Festival, wedding..."
                      className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_date" className="text-white/80">Event Date</Label>
                    <Input
                      id="event_date" type="date" data-testid="booking-date" value={form.event_date} onChange={set("event_date")}
                      className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 h-12"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-white/80">Message *</Label>
                  <Textarea
                    id="message" data-testid="booking-message" value={form.message} onChange={set("message")}
                    placeholder="Tell us about your event..."
                    rows={5}
                    className="mt-2 bg-[#141416] border-white/15 text-white placeholder:text-white/30 resize-none"
                  />
                  {errors.message && <p className="text-[var(--sb-red)] text-xs mt-1">{errors.message}</p>}
                </div>
                <Button
                  type="submit" data-testid="booking-submit" disabled={loading}
                  className="w-full h-14 bg-[var(--sb-red)] hover:bg-[#ff4545] text-white font-bold uppercase tracking-wider rounded-full text-base disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <><Send size={18} className="mr-2" /> Send Enquiry</>}
                </Button>
              </form>
            </Reveal>
          </div>

          {/* Newsletter + info */}
          <div className="flex flex-col gap-6">
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[var(--sb-red)]/15 to-transparent p-8 md:p-10">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--sb-red)]/20 mb-5">
                  <Mail className="text-[var(--sb-red)]" size={22} />
                </div>
                <h3 className="font-display text-4xl text-white">Join The List</h3>
                <p className="text-white/65 mt-3">
                  Be first to hear new music, tour dates and ticket drops. No spam — just the good stuff.
                </p>
                {newsDone ? (
                  <div className="mt-6 flex items-center gap-3 text-white bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="newsletter-success">
                    <CheckCircle2 className="text-[var(--sb-amber)]" size={24} />
                    <span className="font-semibold">You're on the list — see you at the next show!</span>
                  </div>
                ) : (
                  <form onSubmit={submitNews} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form" noValidate>
                    <input type="text" tabIndex={-1} autoComplete="off" value={news.company}
                      onChange={(e) => setNews((n) => ({ ...n, company: e.target.value }))}
                      className="hidden" aria-hidden />
                    <Input
                      data-testid="newsletter-email"
                      value={news.email}
                      onChange={(e) => setNews((n) => ({ ...n, email: e.target.value }))}
                      placeholder="you@email.com"
                      className="bg-[#0a0a0b]/60 border-white/20 text-white placeholder:text-white/30 h-13 py-6"
                    />
                    <Button
                      type="submit" data-testid="newsletter-submit" disabled={newsLoading}
                      className="h-13 py-6 px-7 bg-white text-[#0a0a0b] hover:bg-white/90 font-bold uppercase tracking-wider rounded-full whitespace-nowrap disabled:opacity-60"
                    >
                      {newsLoading ? <Loader2 className="animate-spin" size={20} /> : "Sign Up"}
                    </Button>
                  </form>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="rounded-3xl border border-white/10 bg-[#141416] p-8 flex-1">
                <h3 className="font-display text-3xl text-white">Promoters & Venues</h3>
                <p className="text-white/65 mt-3 leading-relaxed">
                  Whether it's an intimate venue or a grand stage, Songbird delivers an unforgettable,
                  dance-inducing show. Use the booking form and we'll send availability and a press pack.
                </p>
                <div className="mt-6 flex items-end gap-1 h-10" aria-hidden>
                  {[0,1,2,3,4,5,6,7].map((i)=>(
                    <span key={i} className="eq-bar" style={{ animationDelay: `${i*0.1}s`, background: i%2? 'var(--sb-magenta)':'var(--sb-red)' }} />
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
